import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { type LucideIcon } from "lucide-react";

interface Props {
  translationPrefix: string;
  icons: LucideIcon[];
}

const IndustrySolutionI18n = ({ translationPrefix, icons }: Props) => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject, tArray } = useLanguage();
  const features = tObject<Array<{ title: string; description: string }>>(`${translationPrefix}.solution.features`);
  const benefits = tArray(`${translationPrefix}.solution.benefits`);

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn("py-16 md:py-24 bg-background transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center"><Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t(`${translationPrefix}.solution.badge`)}</Badge></div>
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6 text-center">{t(`${translationPrefix}.solution.title`)}</h2>
          <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">{t(`${translationPrefix}.solution.intro`)}</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {features.map((feature, index) => {
              const Icon = icons[index] || icons[0];
              return (
                <div key={index} className={cn("bg-secondary/50 rounded-2xl p-6 text-center transition-all duration-500 hover:bg-secondary/80", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")} style={{ transitionDelay: isVisible ? `${index * 100}ms` : "0ms" }}>
                  <div className="w-14 h-14 rounded-xl bg-chathero-mint/20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-chathero-mint" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              );
            })}
          </div>

          <div className={cn("bg-primary rounded-2xl p-8 text-primary-foreground transition-all duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")} style={{ transitionDelay: isVisible ? "400ms" : "0ms" }}>
            <h3 className="text-xl font-semibold mb-6 text-center">{t(`${translationPrefix}.solution.benefitsTitle`)}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-chathero-mint shrink-0 mt-0.5" />
                  <span className="text-primary-foreground/90">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustrySolutionI18n;
